/**
 * Analytics screen (M9 — FR-10.2, FR-10.3).
 *
 * A richer, cross-module analytics view that loads three datasets in parallel
 * on mount:
 *   1. Dashboard KPIs (`/api/analytics/dashboard`) — rendered as a compact
 *      cards row at the top (gross, net, occupancy, ADR, RevPAR).
 *   2. Channel mix (`/api/analytics/channel-mix`, FR-10.2) — a table with an
 *      inline revenue-share bar per channel, ordered by revenue descending.
 *   3. Booking pace (`/api/analytics/pace`, FR-10.3) — the last 30 days of
 *      reservations as a lightweight inline bar chart scaled to the max count.
 *
 * All backend access goes through the fetch-based `../services.js` layer (web
 * boundary: no backend runtime import). Money arrives as integer-minor STRINGS
 * and is shown via `formatMoney(Number(x), currency)`; ratios are plain
 * numbers. API error envelopes are surfaced through the shared accessible
 * {@link UserMessage} component.
 *
 * SSR-safe: the initial render is a pure loading state; every network call runs
 * inside an effect, so the server render matches hydration.
 */
import { useEffect, useState, type ReactElement } from "react";
import {
  UserMessage,
  type UserMessageModel,
} from "../../components/UserMessage.js";
import { MoneyValue } from "../components/MoneyValue.js";
import {
  apiBookingPace,
  apiChannelMix,
  apiDashboardMetrics,
  asError,
  formatMoney,
  type ChannelMixRow,
  type DashboardMetrics,
  type PaceRow,
} from "../services.js";

/** Fallback currency when a row/summary somehow omits one. */
const FALLBACK_CURRENCY = "INR";

/** Clamp a share ratio in [0,1] to a bar-width percentage string. */
function widthPct(ratio: number): string {
  const clamped = ratio < 0 ? 0 : ratio > 1 ? 1 : ratio;
  return `${(clamped * 100).toFixed(1)}%`;
}

// ===========================================================================
// KPI summary row — a compact reuse of the dashboard metrics
// ===========================================================================

/** A single compact metric card. */
function MetricCard({
  label,
  value,
  moneyMinor,
  currency,
  hint,
  gold = false,
}: {
  readonly label: string;
  /** A pre-formatted non-money value (e.g. occupancy %). */
  readonly value?: string;
  /** For MONETARY KPIs: the amount in minor units + its currency. */
  readonly moneyMinor?: number;
  readonly currency?: string;
  readonly hint?: string;
  /**
   * THE ONE GOLD ELEMENT (brand §07): at most ONE card per view may set this.
   * Gold marks the single headline figure — here, Net revenue.
   */
  readonly gold?: boolean;
}): ReactElement {
  const className = gold ? "console-card console-card--gold" : "console-card";
  return (
    <div className={className}>
      <p className="console-card__label">{label}</p>
      {moneyMinor !== undefined && currency !== undefined ? (
        <MoneyValue minor={moneyMinor} currency={currency} />
      ) : (
        <p className="console-card__value">{value}</p>
      )}
      {hint !== undefined ? <p className="console-card__hint">{hint}</p> : null}
    </div>
  );
}

function KpiRow({
  metrics,
}: {
  readonly metrics: DashboardMetrics;
}): ReactElement {
  const currency = metrics.currency || FALLBACK_CURRENCY;
  return (
    <section className="console-card-grid" aria-label="Key metrics">
      <MetricCard
        label="Gross revenue"
        moneyMinor={Number(metrics.grossRevenueMinor)}
        currency={currency}
        hint="confirmed reservations"
      />
      <MetricCard
        gold
        label="Net revenue"
        moneyMinor={Number(metrics.netRevenueMinor)}
        currency={currency}
        hint="after commission, tax & fees"
      />
      <MetricCard
        label="Occupancy"
        value={`${(metrics.occupancy * 100).toFixed(1)}%`}
        hint="rolling 30-day window"
      />
      <MetricCard
        label="ADR"
        moneyMinor={Number(metrics.adrMinor)}
        currency={currency}
        hint="average daily rate"
      />
      <MetricCard
        label="RevPAR"
        moneyMinor={Number(metrics.revparMinor)}
        currency={currency}
        hint="rolling 30-day window"
      />
    </section>
  );
}

// ===========================================================================
// Channel mix (FR-10.2)
// ===========================================================================

function ChannelMixPanel({
  rows,
}: {
  readonly rows: readonly ChannelMixRow[];
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Channel mix">
      <h2 className="console-panel__title">Channel mix</h2>
      <p className="recon-caption">
        Revenue and room-nights by booking channel, ordered by revenue. The bar
        shows each channel&apos;s share of total confirmed revenue.
      </p>
      {rows.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">
            Revenue share by booking channel
          </caption>
          <thead>
            <tr>
              <th scope="col">Channel</th>
              <th scope="col">Reservations</th>
              <th scope="col">Nights</th>
              <th scope="col">Revenue</th>
              <th scope="col">Share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const pct = `${(row.revenueSharePct * 100).toFixed(1)}%`;
              return (
                <tr key={row.channel}>
                  <td>{row.channel}</td>
                  <td className="recon-num">{row.reservationCount}</td>
                  <td className="recon-num">{row.nights}</td>
                  <td className="recon-num">
                    {formatMoney(Number(row.revenueMinor), FALLBACK_CURRENCY)}
                  </td>
                  <td className="analytics-barcell">
                    <span className="analytics-pct analytics-barcell__value">
                      {pct}
                    </span>
                    <span
                      className="analytics-bar__track"
                      role="img"
                      aria-label={`${row.channel} revenue share ${pct}`}
                    >
                      <span
                        className="analytics-bar"
                        style={{ width: widthPct(row.revenueSharePct) }}
                      />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">
          No channel activity yet — confirmed reservations will appear here.
        </p>
      )}
    </section>
  );
}

// ===========================================================================
// Booking pace (FR-10.3)
// ===========================================================================

function BookingPacePanel({
  rows,
}: {
  readonly rows: readonly PaceRow[];
}): ReactElement {
  const maxCount = rows.reduce(
    (max, row) => (row.reservationCount > max ? row.reservationCount : max),
    0,
  );
  return (
    <section className="console-panel" aria-label="Booking pace">
      <h2 className="console-panel__title">Booking pace</h2>
      <p className="recon-caption">
        Confirmed reservations created per day over the last 30 days. Bars scale
        to the busiest day in the window. Prior-year overlay is deferred.
      </p>
      {rows.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">
            Confirmed reservations created per day
          </caption>
          <thead>
            <tr>
              <th scope="col">Day</th>
              <th scope="col">Reservations</th>
              <th scope="col">Pace</th>
              <th scope="col">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const ratio = maxCount > 0 ? row.reservationCount / maxCount : 0;
              return (
                <tr key={row.day}>
                  <td>{row.day}</td>
                  <td className="recon-num">{row.reservationCount}</td>
                  <td className="analytics-barcell">
                    <span
                      className="analytics-bar__track"
                      role="img"
                      aria-label={`${row.reservationCount} reservations on ${row.day}`}
                    >
                      <span
                        className="analytics-bar"
                        style={{ width: widthPct(ratio) }}
                      />
                    </span>
                  </td>
                  <td className="recon-num">
                    {formatMoney(Number(row.revenueMinor), FALLBACK_CURRENCY)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">No bookings in the last 30 days.</p>
      )}
    </section>
  );
}

// ===========================================================================
// Top-level screen
// ===========================================================================

interface AnalyticsData {
  readonly metrics: DashboardMetrics;
  readonly mix: readonly ChannelMixRow[];
  readonly pace: readonly PaceRow[];
}

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly data: AnalyticsData }
  | { readonly kind: "error"; readonly message: UserMessageModel };

/** Load every analytics dataset in parallel. Metrics are required. */
async function loadAll(): Promise<AnalyticsData> {
  const [metricsResult, mix, pace] = await Promise.all([
    apiDashboardMetrics(),
    apiChannelMix(),
    apiBookingPace(30),
  ]);
  const err = asError(metricsResult);
  if (err !== null) {
    throw new Error(err.error.message);
  }
  const metrics = metricsResult as DashboardMetrics;
  return { metrics, mix, pace };
}

export default function AnalyticsScreen(): ReactElement {
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let active = true;
    loadAll()
      .then((data) => {
        if (active) {
          setState({ kind: "ready", data });
        }
      })
      .catch(() => {
        if (active) {
          setState({
            kind: "error",
            message: {
              what: "Analytics are unavailable right now.",
              why: "The request to the analytics service failed.",
              next: "Check the API server is running and try again.",
            },
          });
        }
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <section aria-labelledby="analytics-heading" data-console-screen="analytics">
      <p className="console-page-eyebrow">Portfolio · Reporting</p>
      <h1 id="analytics-heading">Analytics</h1>
      <p className="console-lede">
        Cross-module reporting on revenue, occupancy, and channel performance —
        the numbers behind the decisions you make.
      </p>

      {state.kind === "loading" ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading analytics…
        </p>
      ) : null}

      {state.kind === "error" ? (
        <UserMessage message={state.message} severity="error" />
      ) : null}

      {state.kind === "ready" ? (
        <>
          <KpiRow metrics={state.data.metrics} />
          <ChannelMixPanel rows={state.data.mix} />
          <BookingPacePanel rows={state.data.pace} />
        </>
      ) : null}
    </section>
  );
}
